import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { recipes } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/RecipeForm.css';

const RecipeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'főétel',
    difficulty: 'közepes',
    prepTime: '',
    cookTime: '',
    servings: '',
    tags: ''
  });

  const [ingredients, setIngredients] = useState([{ name: '', amount: '', unit: '' }]);
  const [instructions, setInstructions] = useState([{ step: 1, description: '' }]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Képfeltöltés state-ek
  const [coverImage, setCoverImage] = useState(null); // Fedőkép File object (új feltöltéshez)
  const [coverImagePreview, setCoverImagePreview] = useState(null); // Fedőkép preview URL
  const [existingCoverImage, setExistingCoverImage] = useState(null); // Meglévő fedőkép URL (szerkesztéshez)
  const [galleryImages, setGalleryImages] = useState([]); // Gallery File objects (új feltöltéshez)
  const [galleryPreviews, setGalleryPreviews] = useState([]); // Gallery preview URLs (új)
  const [existingGalleryImages, setExistingGalleryImages] = useState([]); // Meglévő gallery képek URL-jei

  useEffect(() => {
    // Wait for AuthContext to restore the session before deciding.
    // Child effects run before the provider's, so `user` is still null on first render.
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    if (isEditing) {
      fetchRecipe();
    }
  }, [id, user, authLoading]);

  const fetchRecipe = async () => {
    try {
      const response = await recipes.getById(id);
      const recipe = response.data;

      setFormData({
        title: recipe.title,
        description: recipe.description,
        category: recipe.category,
        difficulty: recipe.difficulty,
        prepTime: recipe.prepTime,
        cookTime: recipe.cookTime,
        servings: recipe.servings,
        tags: recipe.tags?.join(', ') || ''
      });

      setIngredients(recipe.ingredients);
      setInstructions(recipe.instructions.sort((a, b) => a.step - b.step));

      // Load existing cover image
      if (recipe.coverImage) {
        const coverUrl = typeof recipe.coverImage === 'object'
          ? recipe.coverImage.medium
          : recipe.coverImage;
        setExistingCoverImage(coverUrl);
      }

      // Load existing gallery images
      if (recipe.images && recipe.images.length > 0) {
        setExistingGalleryImages(recipe.images);
      }
    } catch (error) {
      console.error('Error fetching recipe:', error);
      setError('Hiba történt a recept betöltése során');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...ingredients];
    newIngredients[index][field] = value;
    setIngredients(newIngredients);
  };

  const addIngredient = () => {
    setIngredients([...ingredients, { name: '', amount: '', unit: '' }]);
  };

  const removeIngredient = (index) => {
    setIngredients(ingredients.filter((_, i) => i !== index));
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...instructions];
    newInstructions[index].description = value;
    setInstructions(newInstructions);
  };

  const addInstruction = () => {
    setInstructions([...instructions, { step: instructions.length + 1, description: '' }]);
  };

  const removeInstruction = (index) => {
    const newInstructions = instructions
      .filter((_, i) => i !== index)
      .map((inst, i) => ({ ...inst, step: i + 1 }));
    setInstructions(newInstructions);
  };

  // Fedőkép kezelés
  const handleCoverImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB

    if (file.size > maxSize) {
      alert(`A kép túl nagy! Maximum 5MB engedélyezett.`);
      return;
    }

    if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
      alert(`Nem támogatott formátum! Csak JPG, PNG, WebP engedélyezett.`);
      return;
    }

    // Preview generálás
    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverImagePreview(reader.result);
      setCoverImage(file);
    };
    reader.readAsDataURL(file);
  };

  const removeCoverImage = () => {
    setCoverImage(null);
    setCoverImagePreview(null);
    setExistingCoverImage(null); // Töröljük a meglévő képet is
  };

  // Gallery képek kezelés
  const handleGalleryImagesSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxImages = 4;
    const maxSize = 5 * 1024 * 1024; // 5MB

    // Maximum 4 kép ellenőrzés
    if (galleryImages.length + files.length > maxImages) {
      alert(`Maximum ${maxImages} gallery kép tölthető fel!`);
      return;
    }

    // Validáció
    const validFiles = [];
    for (const file of files) {
      if (file.size > maxSize) {
        alert(`${file.name} túl nagy! Maximum 5MB engedélyezett.`);
        continue;
      }

      if (!file.type.match(/^image\/(jpeg|jpg|png|webp)$/)) {
        alert(`${file.name} nem támogatott formátum!`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    // Preview generálás
    const newPreviews = [];
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        if (newPreviews.length === validFiles.length) {
          setGalleryPreviews(prev => [...prev, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    });

    setGalleryImages(prev => [...prev, ...validFiles]);
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
    setGalleryPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingGalleryImage = (index) => {
    setExistingGalleryImages(prev => prev.filter((_, i) => i !== index));
  };

  // Közös függvény a FormData készítéséhez
  const prepareFormData = (isDraft = false) => {
    const tagsArray = formData.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag);

    // FormData létrehozása (multipart/form-data)
    const data = new FormData();

    // Text mezők hozzáadása
    data.append('title', formData.title || '');
    data.append('description', formData.description || '');
    data.append('category', formData.category);
    data.append('difficulty', formData.difficulty);
    data.append('prepTime', parseInt(formData.prepTime) || 0);
    data.append('cookTime', parseInt(formData.cookTime) || 0);
    data.append('servings', parseInt(formData.servings) || 1);
    data.append('isDraft', isDraft);

    // Arrays JSON stringként
    data.append('ingredients', JSON.stringify(ingredients.filter(ing => ing.name && ing.amount)));
    data.append('instructions', JSON.stringify(instructions.filter(inst => inst.description)));
    data.append('tags', JSON.stringify(tagsArray));

    // Fedőkép kezelés
    if (coverImage instanceof File) {
      data.append('coverImage', coverImage);
      data.append('keepExistingCover', 'false');
    } else if (existingCoverImage) {
      data.append('keepExistingCover', 'true');
    } else {
      data.append('coverImage', 'null');
      data.append('keepExistingCover', 'false');
    }

    // Meglévő gallery képek megtartása
    if (isEditing && existingGalleryImages.length > 0) {
      data.append('keepExistingImages', JSON.stringify(existingGalleryImages));
    }

    // Új gallery képek hozzáadása
    galleryImages.forEach(file => {
      data.append('images', file);
    });

    return data;
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = prepareFormData(saveAsDraft);

      if (isEditing) {
        await recipes.update(id, data);
        navigate(`/recipe/${id}`);
      } else {
        const response = await recipes.create(data);
        const createdRecipe = response.data;

        if (saveAsDraft) {
          // Navigate to My Recipes with draft success message
          navigate('/my-recipes', {
            state: {
              message: 'Recept piszkozatként mentve! Bármikor visszatérhetsz és folytathatod a szerkesztést.',
              tab: 'drafts'
            }
          });
        } else if (createdRecipe.isApproved) {
          navigate(`/recipe/${createdRecipe._id}`);
        } else {
          navigate('/my-recipes', {
            state: {
              message: 'Recept sikeresen létrehozva! A recept jóváhagyásra vár, amíg egy adminisztrátor ellenőrzi.'
            }
          });
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Hiba történt a recept mentése során');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsDraft = (e) => {
    handleSubmit(e, true);
  };

  return (
    <div className="recipe-form-page">
      <div className="container">
        <h1>{isEditing ? 'Recept Szerkesztése' : 'Új Recept Létrehozása'}</h1>

        <form onSubmit={handleSubmit} className="recipe-form card">
          <div className="form-section">
            <h2>Alapadatok</h2>

            <div className="form-group">
              <label htmlFor="title">Recept címe *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Leírás *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Kategória *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                >
                  <option value="előétel">Előételek</option>
                  <option value="leves">Levesek</option>
                  <option value="főétel">Főételek</option>
                  <option value="saláta">Saláták</option>
                  <option value="desszert">Desszertek</option>
                  <option value="pékáruk">Pékáruk</option>
                  <option value="italok">Italok</option>
                  <option value="egyéb">Egyebek</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="difficulty">Nehézség *</label>
                <select
                  id="difficulty"
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleChange}
                  required
                >
                  <option value="könnyű">Könnyű</option>
                  <option value="közepes">Közepes</option>
                  <option value="nehéz">Nehéz</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="prepTime">Előkészítési idő (perc) *</label>
                <input
                  type="number"
                  id="prepTime"
                  name="prepTime"
                  value={formData.prepTime}
                  onChange={handleChange}
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="cookTime">Főzési idő (perc) *</label>
                <input
                  type="number"
                  id="cookTime"
                  name="cookTime"
                  value={formData.cookTime}
                  onChange={handleChange}
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="servings">Adagok száma *</label>
                <input
                  type="number"
                  id="servings"
                  name="servings"
                  value={formData.servings}
                  onChange={handleChange}
                  min="1"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Diétás címkék</label>
              <div className="diet-tags-checkboxes">
                {['vegetáriánus', 'vegán', 'gluténmentes', 'laktózmentes', 'cukormentes'].map((tag) => (
                  <label key={tag} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.tags.includes(tag)}
                      onChange={(e) => {
                        const currentTags = formData.tags ? formData.tags.split(', ').map(t => t.trim()).filter(t => t) : [];
                        let newTags;
                        if (e.target.checked) {
                          newTags = [...currentTags, tag];
                        } else {
                          newTags = currentTags.filter(t => t !== tag);
                        }
                        setFormData({ ...formData, tags: newTags.join(', ') });
                      }}
                    />
                    {tag.charAt(0).toUpperCase() + tag.slice(1)}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="tags">További címkék (vesszővel elválasztva)</label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleChange}
                placeholder="pl. gyors, egészséges, zöldséges"
              />
              <small className="form-hint">A diétás címkék automatikusan hozzáadódnak</small>
            </div>
          </div>

          <div className="form-section">
            <h2>Hozzávalók</h2>
            {ingredients.map((ingredient, index) => (
              <div key={index} className="ingredient-row">
                <input
                  type="text"
                  placeholder="Mennyiség"
                  value={ingredient.amount}
                  onChange={(e) => handleIngredientChange(index, 'amount', e.target.value)}
                  required
                />
                <input
                  type="text"
                  placeholder="Mértékegység"
                  value={ingredient.unit}
                  onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                />
                <input
                  type="text"
                  placeholder="Hozzávaló neve"
                  value={ingredient.name}
                  onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                  required
                />
                {ingredients.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeIngredient(index)}
                    className="btn-remove"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={addIngredient} className="btn btn-secondary">
              + Hozzávaló hozzáadása
            </button>
          </div>

          <div className="form-section">
            <h2>Elkészítés lépései</h2>
            {instructions.map((instruction, index) => (
              <div key={index} className="instruction-row">
                <span className="step-number">{instruction.step}.</span>
                <textarea
                  placeholder="Írja le a lépést..."
                  value={instruction.description}
                  onChange={(e) => handleInstructionChange(index, e.target.value)}
                  rows="3"
                  required
                />
                {instructions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeInstruction(index)}
                    className="btn-remove"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={addInstruction} className="btn btn-secondary">
              + Lépés hozzáadása
            </button>
          </div>

          <div className="form-section">
            <h2>Fedőkép</h2>
            <p className="image-upload-info">
              A fedőkép jelenik meg a recept listázásokban és a recept tetején.
              <br />
              <strong>💡 Tipp:</strong> Válassz egy szép, jó minőségű képet ami jól mutatja az ételt!
            </p>

            <div className="images-preview-grid">
              {/* Meglévő fedőkép (szerkesztés módban) */}
              {existingCoverImage && !coverImagePreview && (
                <div className="image-preview-item cover-image">
                  <img src={existingCoverImage} alt="Meglévő fedőkép" />
                  <button
                    type="button"
                    onClick={removeCoverImage}
                    className="remove-image-btn"
                  >
                    ✕
                  </button>
                  <span className="cover-badge">⭐ Fedőkép</span>
                </div>
              )}

              {/* Új fedőkép preview */}
              {coverImagePreview && (
                <div className="image-preview-item cover-image">
                  <img src={coverImagePreview} alt="Új fedőkép" />
                  <button
                    type="button"
                    onClick={removeCoverImage}
                    className="remove-image-btn"
                  >
                    ✕
                  </button>
                  <span className="cover-badge">⭐ Új fedőkép</span>
                </div>
              )}

              {/* Fedőkép feltöltés gomb */}
              {!coverImagePreview && !existingCoverImage && (
                <label className="image-upload-box">
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleCoverImageSelect}
                    style={{ display: 'none' }}
                  />
                  <div className="upload-placeholder">
                    <span className="upload-icon">📷</span>
                    <span>Fedőkép hozzáadása</span>
                  </div>
                </label>
              )}

              {/* Csere gomb ha van meglévő */}
              {existingCoverImage && !coverImagePreview && (
                <label className="image-upload-box">
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleCoverImageSelect}
                    style={{ display: 'none' }}
                  />
                  <div className="upload-placeholder">
                    <span className="upload-icon">🔄</span>
                    <span>Csere</span>
                  </div>
                </label>
              )}
            </div>
          </div>

          <div className="form-section">
            <h2>További képek (opcionális)</h2>
            <p className="image-upload-info">
              Tölts fel maximum 4 további képet a receptről (JPG, PNG, WebP, max. 5MB/kép)
              <br />
              <strong>💡 Tipp:</strong> Ezek a képek a recept részletes nézetében jelennek meg galériaként.
            </p>

            <div className="images-preview-grid">
              {/* Meglévő gallery képek (szerkesztés módban) */}
              {existingGalleryImages.map((imgUrl, idx) => (
                <div key={`existing-${idx}`} className="image-preview-item">
                  <img src={imgUrl} alt={`Meglévő kép ${idx + 1}`} />
                  <button
                    type="button"
                    onClick={() => removeExistingGalleryImage(idx)}
                    className="remove-image-btn"
                  >
                    ✕
                  </button>
                </div>
              ))}

              {/* Új gallery képek preview */}
              {galleryPreviews.map((preview, idx) => (
                <div key={`new-${idx}`} className="image-preview-item">
                  <img src={preview} alt={`Új kép ${idx + 1}`} />
                  <button
                    type="button"
                    onClick={() => removeGalleryImage(idx)}
                    className="remove-image-btn"
                  >
                    ✕
                  </button>
                  <span className="cover-badge">🆕 Új</span>
                </div>
              ))}

              {/* Gallery képek feltöltés gomb */}
              {(existingGalleryImages.length + galleryImages.length) < 4 && (
                <label className="image-upload-box">
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    multiple
                    onChange={handleGalleryImagesSelect}
                    style={{ display: 'none' }}
                  />
                  <div className="upload-placeholder">
                    <span className="upload-icon">📷</span>
                    <span>Képek hozzáadása ({existingGalleryImages.length + galleryImages.length}/4)</span>
                  </div>
                </label>
              )}
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button type="button" onClick={() => navigate(-1)} className="btn btn-outline">
              Mégse
            </button>
            {!isEditing && (
              <button
                type="button"
                onClick={handleSaveAsDraft}
                className="btn btn-secondary"
                disabled={loading}
                title="Mentés piszkozatként - később folytathatod a szerkesztést"
              >
                {loading ? 'Mentés...' : '📝 Mentés piszkozatként'}
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Mentés...' : isEditing ? 'Módosítások mentése' : 'Recept létrehozása'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecipeForm;
